import getAxiosClient from "@lib/getAxiosClient";
import {
  AttributeValueType,
  BasketType,
  Currency,
  FinalMileMeta,
  GQLResponse,
  TransportPriority,
  UpdateConfigItemThumbnailsPayload,
} from "@sourceful/shared-types";
import { apiConfig } from "../../../apiConfig";

import { ConfigurationItemQueryVariables, ConfigurationItemQuery } from "@generated/graphql";
import { CONFIGURATION_ITEM_QUERY } from "@gql/queries/configuration/getConfigItem";
import { graphQLRequest, useGraphQLPrep } from "@lib/hooks/useGraphQLRequest";
import GuestConfigItemManager from "@lib/GuestConfigItemManager/GuestConfigItemManager";
import { EngineFetchReason, _applyEngineResultsToItem } from "@sourceful/shared-utils/engine-utils";
import { engineAxiosClient } from "@providers/BasketProvider/helpers";
import { getEngineVersion } from "@lib/getEngineVersion";

export * from "./updateConfigItemRecord";

interface Args {
  token?: string;
  payload: {
    product_attributes: { [index: string]: AttributeValueType | AttributeValueType[] };
    base_product_id: number;
    version_id: number;
    step_reference: string;
    locale: string;
  };
}

interface GalleryImageResponse {
  images: { caption: string; url: string }[] | null;
}

export const fetchConfiguratorGalleryImages = async ({
  token,
  payload,
}: Args): Promise<GalleryImageResponse> => {
  const axios = getAxiosClient(token, {
    baseURL: apiConfig.blueprintApiOrigin,
  });

  const response = await axios.post(`/gallery/v2`, payload);

  return response.data;
};

type SubmitQuoteForBasketItemArgs = {
  token?: string;
  basketItemId: string;
  includeCarbonOffset?: boolean;
};

interface BasketRecord {
  id: number | string;
  type: BasketType;
  currency: Currency;
  carbon_offset: boolean;
  status_id: number;
  basket_status_name?: string;
  delivery_address_id: string | null;
  user_id: string;
  last_updated: string;
  autostock_requested: boolean;
}

export const submitQuoteForBasketItem = async ({
  token,
  basketItemId,
  includeCarbonOffset = false,
}: SubmitQuoteForBasketItemArgs): Promise<{
  newQuote: BasketRecord;
}> => {
  const axios = getAxiosClient(token, {
    baseURL: apiConfig.productCloudApiOrigin,
  });

  const response = await axios.post("/quotes/custom-quote", {
    basketItemId,
    includeCarbonOffset,
  });

  return response.data;
};

interface SubmitStudioArgs {
  token: string;
  payload: {
    configItemId: string;
    postcode: string;
    countryCode: string;
    gdsVersion: string;
    includeCarbonOffset: boolean;
    createArtworkZip: boolean;
  };
}

export const submitStudioQuote = async ({
  token,
  payload,
}: SubmitStudioArgs): Promise<{
  newQuote: BasketRecord;
}> => {
  const axios = getAxiosClient(token, {
    baseURL: apiConfig.productCloudApiOrigin,
  });

  const response = await axios.post("/quotes/studio-quote", payload);

  return response.data;
};

type SubmitQuoteForBasketArgs = {
  token?: string;
  basketId: string;
  includeCarbonOffset?: boolean;
};

export const submitQuoteForBasket = async ({
  token,
  basketId,
  includeCarbonOffset = false,
}: SubmitQuoteForBasketArgs) => {
  const axios = getAxiosClient(token, {
    baseURL: apiConfig.productCloudApiOrigin,
  });

  const response = await axios.post("/quotes/custom-quote", {
    basketId,
    includeCarbonOffset,
  });

  return response.data;
};

interface UpdateConfigItemRecordThumbnailsArgs {
  token: string;
  payload: UpdateConfigItemThumbnailsPayload;
}

export const updateConfigItemRecordThumbnails = async ({
  token,
  payload,
}: UpdateConfigItemRecordThumbnailsArgs) => {
  const client = getAxiosClient(token, {
    baseURL: apiConfig.productCloudApiOrigin,
  });

  const res = await client.put("/configuration-item/thumbnails", payload);

  return res?.data;
};

export const useFetchRawConfigItem = () => {
  const { getAccessTokenSilently, operationName, rawQuery, role } =
    useGraphQLPrep(CONFIGURATION_ITEM_QUERY);
  const fetchRawConfigItem = async (variables: ConfigurationItemQueryVariables) => {
    const res = await graphQLRequest<
      ConfigurationItemQueryVariables,
      GQLResponse<ConfigurationItemQuery>
    >(rawQuery, {
      token: await getAccessTokenSilently(),
      operationName,
      role,
      variables,
    });

    return res?.data?.product_cloud_configuration_item[0] || null;
  };

  return [fetchRawConfigItem];
};

export interface GetDraftConfigItemArgs {
  configItemId: string;
  finalMile: FinalMileMeta;
  activeAttributeId: string | null;
}
export const getDraftConfigItem = async ({
  configItemId,
  finalMile,
  activeAttributeId,
}: GetDraftConfigItemArgs) => {
  const draftsManager = new GuestConfigItemManager();
  const draft = await draftsManager.getItem();
  if (!draft || draft.id !== configItemId) {
    return null;
  }

  console.log("decorating raw config item");
  let decoratedItem = await _applyEngineResultsToItem({
    client: engineAxiosClient,
    item: draft,
    engineFetchType: { type: EngineFetchReason.INITIAL_FETCH },
    activeAttributeId,
    engineOptions: {
      postcode: finalMile.postcode,
      split_delivery_quantities: draft?.metadata?.splitDeliveryQuantities,
      transportPriority: draft?.metadata?.transportPriority || TransportPriority.PRICE,
    },
    engineVersion: getEngineVersion(),
  });

  // // we only persist the draft temporarily so we can load into configurator v2 and persist the item in between page loads
  // // the page needs to be reloaded in order to load new design system critical css
  draftsManager.removeItem();

  return decoratedItem;
};
